/**
 * The access-reason field a payslip PDF download waits on — roadmap U26.
 *
 * The field `EmployeeDocumentsPanel` puts above its downloads, with the same rules
 * (`access/reason.ts`): at least ten characters after trimming; Latin-1 only, because a
 * header value is a ByteString; and the shared `access.*` sentences saying where the
 * reason goes. It gates the download controls, which are the one place a disabled control
 * is honest: "you have not finished saying what you are doing".
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { isHeaderSafe, MAX_ACCESS_REASON, MIN_ACCESS_REASON } from '../../access/reason';

export interface PayslipReasonFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** The block's id; the input is `${testId}-input`, the rule `${testId}-rule`. */
  readonly testId: string;
}

export function PayslipReasonField({
  value,
  onChange,
  testId,
}: PayslipReasonFieldProps): ReactNode {
  const { t } = useI18n();
  // Told apart from "too short": a different problem with a different remedy — see
  // `AccessReasonGate`.
  const unencodable = value.trim() !== '' && !isHeaderSafe(value.trim());
  const inputId = `${testId}-input`;
  return (
    <div className="wp-section__subsection" data-testid={testId}>
      <p className="wp-small">{t('access.why', { subject: t('payroll.pdf.subject') })}</p>
      <p className="wp-small wp-muted">{t('access.recorded')}</p>
      <div className="wp-field">
        <label className="wp-field__label" htmlFor={inputId}>
          {t('access.label')}
        </label>
        <input
          className="wp-field__input"
          id={inputId}
          data-testid={inputId}
          type="text"
          value={value}
          maxLength={MAX_ACCESS_REASON}
          autoComplete="off"
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        <p className="wp-small wp-muted" data-testid={`${testId}-rule`}>
          {t('access.rule', { min: MIN_ACCESS_REASON })}
        </p>
        {unencodable ? (
          <p className="wp-small" role="alert" data-testid={`${testId}-latin1`}>
            {t('access.latin1')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
